<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import PropertySection from './PropertySection.vue'
import PropertyGallery from './PropertyGallery.vue'
import { MEDIA_TYPE } from '../../constants/propertyLifecycle'
import type { PropertyDetail } from '../../types/propertyTypes'

// Photos and video for the property — the public ones, as investors see them.
//
// Unlike every other section on this page, this one renders even when it is
// empty. "Where are my photos?" is a question an owner actually asks, and
// silence answers it with an implication — that something is broken, or that
// they were supposed to upload them. The empty state says who prepares them,
// which is the real answer.
const props = defineProps<{ property: PropertyDetail }>()

const media = computed(() => props.property.media)

const photoCount = computed(
  () => media.value.filter((item) => item.mediaType !== MEDIA_TYPE.video).length,
)
const videoCount = computed(
  () => media.value.filter((item) => item.mediaType === MEDIA_TYPE.video).length,
)

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

const summary = computed(() => {
  const parts: string[] = []
  if (photoCount.value) parts.push(plural(photoCount.value, 'photo'))
  if (videoCount.value) parts.push(plural(videoCount.value, 'video'))
  return parts.join(' · ')
})
</script>

<template>
  <PropertySection title="Photos & media" :subtitle="summary || undefined">
    <PropertyGallery v-if="media.length" :media="media" />

    <Box v-else direction="col" align="center" gap="2" class="media-empty">
      <PiIcon name="images" class="media-empty__icon" />
      <Text type="body" weight="medium">Nothing published yet</Text>
      <Text type="caption" color="muted" align="center" class="media-empty__hint">
        Sliceal is preparing enrichment materials for your property. Photos, videos and documents
        appear here once they’re published on the Marketplace.
      </Text>
    </Box>
  </PropertySection>
</template>

<style scoped>
.media-empty {
  padding: 2.5rem 1rem;
  border: 1px dashed var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  background: var(--p-surface-50, var(--p-surface-0));
}

.media-empty__icon {
  font-size: 1.5rem;
  color: var(--p-text-muted-color);
}

.media-empty__hint {
  max-width: 28rem;
}
</style>
